
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { profileService } from '@/services/profileService';
import type { Database } from '@/integrations/supabase/types';

type UserProfile = Database['public']['Tables']['user_profiles']['Row'];
type UserProfileUpdate = Database['public']['Tables']['user_profiles']['Update'];

export const useProfile = (userId?: string) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  
  // If userId is provided, fetch that user's profile, otherwise fetch current user
  const targetUserId = userId || user?.id;
  const isOwnProfile = !userId || userId === user?.id;

  const { data: profile, isLoading: loading, error, refetch } = useQuery({
    queryKey: ['profile', targetUserId],
    queryFn: () => userId ? profileService.getUserProfile(userId) : profileService.getCurrentUserProfile(),
    enabled: !!targetUserId,
  });

  const mutation = useMutation({
    mutationFn: (updates: UserProfileUpdate) => profileService.updateProfile(updates),
    onSuccess: (updatedProfile) => {
      if (updatedProfile) {
        queryClient.setQueryData(['profile', user?.id], updatedProfile);
      }
      // Invalidate related queries to refresh data across the app
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserRank'] });
    },
    onError: (err) => {
      console.error('Error updating profile:', err);
    },
  });

  return {
    profile: profile || null,
    loading,
    error: error ? (error as Error).message : null,
    updateProfile: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    refetch,
    isOwnProfile,
  };
};
